import React, { useState, useEffect } from 'react';
import { Linkedin, Instagram, Mail, Rocket, BookOpen, Music, PocketKnife } from 'lucide-react';

export default function Portfolio() {
  const [currentObsession, setCurrentObsession] = useState(0);
  const [educationOpen, setEducationOpen] = useState(false);

  const obsessions = [
    { icon: <Rocket size={20} />, text: "Aerospace engineering" },
    { icon: <BookOpen size={20} />, text: "Philosophy & debates" },
    { icon: <Music size={20} />, text: "Desi hip-hop on repeat" },
    { icon: <PocketKnife size={20} />, text: "Crime Podcasts" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentObsession(prev => (prev + 1) % 4);
    }, 3000);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white text-black min-h-screen font-sans relative overflow-hidden">
      {/* Abstract Background Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0">
        <svg className="w-full h-full">
          <circle cx="10%" cy="20%" r="150" fill="black" />
          <circle cx="90%" cy="80%" r="200" fill="black" />
          <path d="M 0,100 Q 150,50 300,100 T 600,100" stroke="black" strokeWidth="2" fill="none" />
          <line x1="80%" y1="10%" x2="90%" y2="30%" stroke="black" strokeWidth="1" />
          <line x1="15%" y1="70%" x2="25%" y2="90%" stroke="black" strokeWidth="1" />
        </svg>
      </div>

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white border-b-4 border-black z-50">
        <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
          <div className="text-3xl font-black tracking-tighter">MW</div>
          
          <div className="flex space-x-6 md:space-x-12 text-xs md:text-base tracking-wider uppercase font-black">
            <button onClick={() => scrollToSection('about')} className="hover:underline hover:italic">
              ABOUT
            </button>
            <button onClick={() => setEducationOpen(true)} className="hover:underline hover:italic">
              EDUCATION
            </button>
            <button onClick={() => scrollToSection('stuff')} className="hover:underline hover:italic">
              STUFF
            </button>
            <button onClick={() => scrollToSection('work')} className="hover:underline hover:italic">
              WORK
            </button>
            <button onClick={() => scrollToSection('contact')} className="hover:underline hover:italic">
              CONTACT
            </button>
          </div>
        </div>
      </nav>

      {/* Education Dialog */}
      {educationOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-6" onClick={() => setEducationOpen(false)}>
          <div className="bg-white border-4 border-black max-w-3xl w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setEducationOpen(false)} className="absolute top-4 right-4 text-3xl font-black hover:rotate-90 transition-transform">
              ×
            </button>
            
            <h2 className="text-5xl font-black mb-8 tracking-tighter">EDUCATION</h2>
            
            <div className="space-y-8">
              <div className="border-4 border-black p-6 relative">
                <div className="absolute -top-4 -left-4 bg-white px-4 py-1 font-black text-sm border-2 border-black">PRESENT</div>
                <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                  <div>
                    <h3 className="text-2xl font-black mb-2">B.TECH — ECE</h3>
                    <p className="font-light">Amrita School of Engineering, Bengaluru</p>
                    <p className="text-sm mt-2 font-mono">2nd Year</p>
                  </div>
                  <div className="text-right mt-4 md:mt-0">
                    <p className="text-4xl font-black">9.21</p>
                    <p className="text-xs font-mono">CGPA</p>
                  </div>
                </div>
              </div>

              <div className="border-2 border-black p-6 opacity-70">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                  <div>
                    <h3 className="text-2xl font-black mb-2">SCHOOL — CBSE</h3>
                    <p className="font-light">Bharath Vidya Mandir, Tenkasi (Tamil Nadu)</p>
                  </div>
                  <div className="text-right mt-4 md:mt-0">
                    <p className="text-4xl font-black">90.6%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-20 relative">
        {/* Sketch Lines */}
        <div className="absolute top-20 right-10 w-32 h-32 border-2 border-black opacity-20 rotate-12"></div>
        <div className="absolute bottom-40 left-10 w-24 h-24 opacity-10">
          <svg viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" stroke="black" strokeWidth="2" fill="none" strokeDasharray="5,5" />
          </svg>
        </div>

        <div className="max-w-6xl w-full grid md:grid-cols-2 gap-16 items-center z-10">
          {/* Photo with Abstract Frame */}
          <div className="flex justify-center md:justify-end order-2 md:order-1 relative">
            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-black opacity-30"></div>
            <div className="w-72 h-72 md:w-96 md:h-96 bg-black relative overflow-hidden">
              <img 
                src="/pic2.jpg" 
                alt="Madhumitha Winsraj" 
                className="w-full h-full object-cover"
              />
              {/* Sketch overlay */}
            
            </div>
          </div>

          {/* Text */}
          <div className="space-y-6 order-1 md:order-2">
            <div className="inline-block border-2 border-black px-4 py-1 text-xs font-bold tracking-widest mb-4">
              BENGALURU / 18
            </div>
            <h1 className="text-6xl md:text-7xl font-black leading-none tracking-tighter">
              MADHUMITHA<br />WINSRAJ
            </h1>
            <p className="text-xl font-light tracking-wide border-l-4 border-black pl-4 italic">
              Circuits, mechanics, and mildly controversial opinions.
            </p>
            <div className="flex items-center space-x-3 text-sm font-mono animate-pulse">
              <span className="w-2 h-2 bg-black rounded-full"></span>
              <span>Currently obsessed with:</span>
              <span className="font-bold flex items-center space-x-2">
                {obsessions[currentObsession].icon}
                <span>{obsessions[currentObsession].text}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-6 relative">
        <div className="absolute top-10 right-20 text-9xl font-black opacity-5 -rotate-12">?</div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-6xl font-black mb-12 tracking-tighter">WHO AM I?</h2>
          <div className="space-y-6 text-lg leading-relaxed">
            <p className="font-light">
              I'm Madhumitha — an 18yo ECE student who's genuinely interested in more things than I have time for. Currently maintaining a 9.21 CGPA and exploring a lot of Mechanics and Aerospace (started recently, already hooked).
            </p>
            <p className="font-light border-l-4 border-black pl-6 italic">
              Robotics, IoT, hardware systems, AI, applied math — if it involves building or breaking things, I'm in.
            </p>
            <p className="font-light">
              When I'm not elbow-deep in circuits or aerospace calculations, I'm usually sketching, reading, debating, or listening to desi hip-hop. Competitive by nature, mildly sarcastic, and always curious about how things are built.
            </p>
            <p className="font-bold text-xl mt-8">
              Building the things I once Googled. Badly quoting philosophy.
            </p>
          </div>
        </div>
      </section>

      {/* Stuff I Do Section */}
      <section id="stuff" className="py-32 px-6 bg-black text-white relative">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <svg className="w-full h-full">
            <pattern id="dots" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
              <circle cx="25" cy="25" r="2" fill="white" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-6xl font-black mb-16 tracking-tighter">STUFF I DO</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: "AEROSPACE & MECHANICS", desc: "Just started studying it. Already in love. Thermoacoustic Instability? *Chef's kiss*" },
              { title: "ROBOTICS, AI & IoT", desc: "Building things that move and think and are actually useful. Currently working on an AI-powered Smart Cap that can make Blind people go hands-free (NO CANE YAYY)." },
              { title: "ART", desc: "I've always got a Pencil and paper but no patience around me. I do Abstract, Symbolism, Realism what and what not?!" },
              { title: "PHILOSOPHY & DEBATE", desc: "Love a good argument. Even better if it's about existentialism at 2 AM. (I'm a Nihilist Btw)" },
              { title: "BOOKS & DOCS", desc: "Always reading something, it's either spiralling over life or obsessing over crime." },
              { title: "MUSIC", desc: "I go from 'Naraz chaand hai ya taaron ki kami?' to 'I'll bully you and show you who's boss' real quick." }
            ].map((item, idx) => (
              <div key={idx} className="border-2 border-white p-6 hover:bg-white hover:text-black transition-all duration-300 group relative">
                <h3 className="text-2xl font-black mb-3 tracking-tight">{item.title}</h3>
                <p className="font-light text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Experience */}
      <section id="work" className="py-32 px-6 bg-white relative">
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-6xl font-black mb-16 tracking-tighter">WORK</h2>
          
          <div className="space-y-12">
            {[
              {
                role: "STEERING TEAM MEMBER",
                org: "GKDC'26",
                period: "AUG 2025 – PRESENT",
                desc: "Ackermann geometry calculations, steering column design, material testing, and paddle shifters designing.",
                highlight: true
              },
              {
                role: "VICE PRESIDENT",
                org: "GOOGLE DSC ASEB",
                period: "MAY 2025 – PRESENT",
                desc: "Coordinating events, speaker sessions, technical initiatives. Managing teams. Guiding devs and designers.",
                highlight: true
              },
              {
                role: "WEB DEVELOPER",
                org: "TEAM BI0S",
                period: "JAN 2025 – PRESENT",
                desc: "Linux workflows, Git, open-source. Frontend + backend basics. Figma & DB fundamentals.",
                highlight: true
              },
              {
                role: "EMBEDDED SUBSYSTEM",
                org: "TEAM ROVER ASEB",
                period: "JUL 2025 – OCT 2025",
                desc: "PCB design. FMEA. Component testing."
              },
              {
                role: "SPONSORSHIP TEAM",
                org: "AMRITA ROBOTICS",
                period: "FEB 2025 – AUG 2025",
                desc: "Outreach. Proposal writing. Sponsor communication."
              }
            ].map((exp, idx) => (
              <div key={idx} className={`border-l-4 ${exp.highlight ? 'border-black' : 'border-gray-400'} pl-8 relative`}>
                <div className={`absolute -left-2 top-0 w-3 h-3 ${exp.highlight ? 'bg-black' : 'bg-gray-400'} rounded-full`}></div>
                <div className="text-xs font-mono mb-2 text-gray-600">{exp.period}</div>
                <h3 className="text-3xl font-black mb-1 tracking-tight">{exp.org}</h3>
                <p className="text-sm font-bold mb-3 tracking-widest">{exp.role}</p>
                <p className="font-light text-gray-700">{exp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-32 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-6xl font-black mb-12 tracking-tighter">LET'S TALK</h2>
          
          <p className="text-xl font-light mb-12 leading-relaxed">
            Want to collaborate? Debate philosophy? Talk aerospace? Hit me up.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <Linkedin size={28} />, label: "LINKEDIN", link: "https://www.linkedin.com/in/madhumitha-winsraj" },
              { icon: <Instagram size={28} />, label: "INSTAGRAM", link: "https://www.instagram.com/madhuwinsraj/" },
              { icon: <Mail size={28} />, label: "EMAIL", link: "mailto:mathu1413@gmail.com" },
              { 
                icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>, 
                label: "DISCORD", 
                link: "https://discord.com" 
              }
            ].map((contact, idx) => (
              <a 
                key={idx}
                href={contact.link}
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white p-6 flex flex-col items-center justify-center hover:bg-white hover:text-black transition-all duration-300 group"
              >
                {contact.icon}
                <span className="text-xs font-black mt-3 tracking-widest">{contact.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-black text-white border-t-4 border-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-xs font-mono">
          <p>© 2025 MADHUMITHA WINSRAJ</p>
          <p className="italic">Built with 3 A.M. motivation and chocos.</p>
        </div>
      </footer>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;700;900&display=swap');
        
        * {
          font-family: 'Space Grotesk', sans-serif;
        }
      `}</style>
    </div>
  );
}